import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Result } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import '../../styles/AuthGuard.css';
import { useMenuPermission } from '../../hooks/useMenuPermission';

// Route names do not always match the module/menuPermission key.
// Keep only real route aliases here; ordinary plural/singular differences are
// already handled by useMenuPermission.
const ROUTE_MODULE_ALIASES = {
  'candidate-onboarding': 'onboarding',
  'candidate-assignment': 'assignments',
  candidate: 'candidates',
  job: 'jobs',
  submission: 'submissions',
  client: 'clients',
  employer: 'employers',
  referral: 'referrals',
  timesheet: 'timesheets',
};

// These are shell/utility pages rather than permission-backed business
// modules. They must not be denied just because menuPermission has no entry.
const UNGUARDED_ROUTES = new Set([
  '',
  'dashboard',
  'my-profile',
  'profile',
  'viewall-sticky',
  'select-project',
  'addtestcase',
  'managemodules',
  'viewtestcase',
  'test-cases',
  'zinnext-home',
  'lms-home',
  'test',
]);

function firstPathSegment(pathname) {
  return String(pathname ?? '')
    .split('?')[0]
    .split('#')[0]
    .replace(/^\/+/, '')
    .split('/')[0]
    .trim()
    .toLowerCase();
}

export function getPermissionModuleFromPath(pathname) {
  const segment = firstPathSegment(pathname);
  if (!segment || UNGUARDED_ROUTES.has(segment)) return '';

  // ListView's default Add route uses the flat `/<module>-create` convention.
  // Permission data is stored against the module itself (for example
  // `batches`, not `batches-create`). Resolve that suffix before checking
  // aliases so direct URLs such as /batches-create are evaluated against the
  // same permission entry as /batches.
  const createSuffix = '-create';
  const permissionSegment = segment.endsWith(createSuffix)
    ? segment.slice(0, -createSuffix.length)
    : segment;

  return ROUTE_MODULE_ALIASES[permissionSegment] ?? permissionSegment;
}

/**
 * Prevents direct URL access when the logged-in role has View = 0.
 *
 * Sidebar filtering is only presentation. This guard enforces the same
 * permission at page-render time, so typing/pasting a hidden module URL does
 * not bypass the role configuration.
 */
export default function RoutePermissionGuard({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const moduleName = getPermissionModuleFromPath(location.pathname);
  const { canView } = useMenuPermission(moduleName);

  if (moduleName && !canView) {
    const moduleLabel = moduleName
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());

    return (
      <div className="ct-auth-guard">
        <div className="ct-auth-guard__card">
          <Result
            status="403"
            icon={<LockOutlined className="ct-auth-guard__icon" />}
            title="Access Denied"
            subTitle={`You do not have View access to the ${moduleLabel} module. Please contact your administrator if you need access.`}
            extra={(
              <Button
                type="primary"
                size="large"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
            )}
          />
        </div>
      </div>
    );
  }

  return children;
}
